/**
 * transaction-type.ts - User-Editable Transaction Type Entity
 *
 * This file defines the Firestore entity for user-editable transaction types.
 * The authoritative type system is TransactionL0 (8 Korean types in category-v2.ts).
 *
 * ⚠️ TIMESTAMP FORMAT: This entity uses ISO 8601 string timestamps (e.g., "2024-12-30T12:00:00.000Z")
 * for simpler serialization. For Firestore-native Timestamp, see classification.ts.
 *
 * ┌─────────────────────────────────────────────────────────────────────────┐
 * │ TransactionType (this file)    vs.    TransactionL0 (L0_CONFIGS)       │
 * ├─────────────────────────────────────────────────────────────────────────┤
 * │ User-editable CRUD entity             Static configuration constants   │
 * │ Stored in Firestore                   Defined in category-v2.ts        │
 * │ /transaction-types 페이지              리포트, 분류 시스템              │
 * │ 사용자 커스텀 타입 가능                 8개 고정 L0 타입                 │
 * └─────────────────────────────────────────────────────────────────────────┘
 *
 * Use Cases:
 * - /transaction-types 페이지 CRUD 기능
 * - 사용자 커스텀 타입 생성 및 관리
 * - 시스템 타입 초기화 (사용자 가입 시)
 *
 * Related Files:
 * - category-v2.ts: L0_CONFIGS - 8개 L0 타입 설정 (리포트, 분류에 사용)
 * - transactionTypeService.ts: 이 엔티티의 CRUD 서비스
 *
 * Design Decision (from research.md):
 * - Separate `transaction_types` collection for flexibility and query performance
 * - Types can be system-defined (수입, 지출, etc.) or user-created
 * - Transaction snapshots preserve historical type names
 */

/**
 * Transaction Type entity
 * Highest level of category hierarchy (Type > L1 > L2)
 */
export interface TransactionType {
  id: string; // Unique identifier
  userId: string; // Owner of this type
  name: string; // Display name (e.g., "수입", "지출", "이체", "카드결제")
  displayOrder: number; // Sort order in UI (ascending)
  isSystemType: boolean; // System-defined (true) vs. user-created (false)
  isActive: boolean; // Soft delete flag
  createdAt: string; // ISO timestamp
  updatedAt?: string; // ISO timestamp
}

/**
 * Transaction Type form data (for create/edit operations)
 */
export interface TransactionTypeFormData {
  name: string; // Required, max 50 chars
  displayOrder?: number; // Optional, defaults to max + 1
  isActive?: boolean; // Optional, defaults to true
}

/**
 * Transaction Type validation result
 */
export interface TransactionTypeValidation {
  isValid: boolean;
  errors: {
    field: string;
    message: string;
  }[];
}

/**
 * Transaction Type delete preview
 * Shows impact of deleting a type before confirmation
 */
export interface TransactionTypeDeletePreview {
  typeId: string;
  typeName: string;
  l1CategoryCount: number; // Number of L1 categories under this type
  l2CategoryCount: number; // Number of L2 categories under this type
  transactionCount: number; // Number of transactions using this type
  canDelete: boolean; // false if transactionCount > 0 or child categories exist
  warningMessage?: string; // User-facing warning message
}

/**
 * System-defined transaction types
 * These are created automatically on user signup
 *
 * Order matches TransactionL0/DefaultL0Category (category-v2.ts):
 * 지출, 수입, 카드결제, 이체, 대출상환, 대출인출, 사업지출, 사업수입
 */
export const SYSTEM_TRANSACTION_TYPES = [
  { name: '지출', displayOrder: 1 },
  { name: '수입', displayOrder: 2 },
  { name: '카드결제', displayOrder: 3 },
  { name: '이체', displayOrder: 4 },
  { name: '대출상환', displayOrder: 5 },
  { name: '대출인출', displayOrder: 6 },
  { name: '사업지출', displayOrder: 7 },
  { name: '사업수입', displayOrder: 8 },
] as const;
